/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import ReactTooltip from "react-tooltip";
import { Instance } from "tippy.js";
import AlignJustifyIcon from "../../../../assets/icon/CampaignNotesToolbar/align-justify-icon";
import AlignLeftIcon from "../../../../assets/icon/CampaignNotesToolbar/align-left-icon";
import AlignMiddleIcon from "../../../../assets/icon/CampaignNotesToolbar/align-middle-icon";
import AlignRightIcon from "../../../../assets/icon/CampaignNotesToolbar/align-right-icon";
import BoldIcon from "../../../../assets/icon/CampaignNotesToolbar/BoldIcon";
import ChecklistIcon from "../../../../assets/icon/CampaignNotesToolbar/checklist-icon";
import FormatTextIcon from "../../../../assets/icon/CampaignNotesToolbar/format-text-icon";
import HorizontalSeparatorIcon from "../../../../assets/icon/CampaignNotesToolbar/horizontal-separator-icon";
import IndentIcon from "../../../../assets/icon/CampaignNotesToolbar/indent-icon";
import ItalicIcon from "../../../../assets/icon/CampaignNotesToolbar/ItalicIcon";
import LinkAttachIcon from "../../../../assets/icon/CampaignNotesToolbar/link-attach-icon";
import OutdentIcon from "../../../../assets/icon/CampaignNotesToolbar/outdent-icon";
import { EmailMode } from "../../../../atoms/EmailAtom";
import useClickOutside from "../../../../hook/useClickOutside";
import useLinkPopover from "../../../../hook/useLinkPopover";
import { notSelect } from "../../../../styles/general";
import {
  getSelectedAnchorElement,
  getSelectedEditorText,
  getSelectedRange,
} from "../../../../utils/LinkUtils";
import MergeFields from "../../../NewEmail/MergeFields";
import useNoteLinkPopover from "../NoteEditor/useNoteLinkPopover";
import EditToolbarButton from "./EditToolbarButton";
import HeadingButton from "./HeadingButton";
import ListButton from "./ListButton";
import NotePopoverFormatting from "./NotePopoverFormatting";
import { handleBold, handleItalic, isElementActive } from "./toolbarUtils";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% + 50px);
  height: 45px;
  background-color: #f9f9f9;
  margin: 18px 0px;
  border-top: 1px solid rgba(112, 112, 112, 0.15);
`;

const ToolbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 372px;
  height: 45px;
`;

const ToolbarItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  cursor: pointer;
  height: 100%;
  width: 34px;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Divider = styled.div`
  height: 20px;
  width: 1px;
  background-color: #dddddd;
`;

const TooltipStyles = css`
  width: fit-content !important;
  padding: 2px 4px !important;
  margin-top: 5px !important;
  ${notSelect}
`;

enum AlignType {
  LEFT = "Left",
  MIDDLE = "Middle",
  RIGHT = "Right",
  JUSTIFY = "Justify",
}

const IconWrapper = styled.div`
  :hover {
    > svg > rect {
      fill: #eaeaea;
    }
  }

  > svg > rect {
    fill: #f9f9f9;
  }
`;

interface NoteModalToolbarProps {
  editor: Editor;
  tooltips?: React.MutableRefObject<{ [href: string]: Instance }>;
  disableTaskList?: boolean;
  mode?: EmailMode;
  index?: number;
  isNoteEditor?: boolean;
}

export default function EditorToolbar({
  editor,
  tooltips,
  disableTaskList,
  mode,
  index,
  isNoteEditor,
}: NoteModalToolbarProps) {
  const { openLinkModal } = isNoteEditor
    ? useNoteLinkPopover(editor, tooltips)
    : useLinkPopover(editor, tooltips, mode, index);
  const [openDropView, setOpenDropView] = useState(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));

  const textAlign = editor?.isActive({ textAlign: "left" })
    ? "left"
    : editor?.isActive({ textAlign: "right" })
    ? "right"
    : editor?.isActive({ textAlign: "center" })
    ? "center"
    : "justify";

  const getAlignItem = useMemo(() => {
    switch (textAlign) {
      case "left":
        return <AlignLeftIcon />;
      case "center":
        return <AlignMiddleIcon />;
      case "right":
        return <AlignRightIcon />;
      case "justify":
        return <AlignJustifyIcon />;
      default:
        return <AlignLeftIcon />;
    }
  }, [textAlign]);

  const handleAlignTypes = useCallback(() => {
    switch (textAlign) {
      case "left":
        editor.chain().setTextAlign("center").run();
        break;

      case "center":
        editor.chain().setTextAlign("right").run();
        break;

      case "right":
        editor.chain().setTextAlign("justify").run();
        break;

      default:
        editor.chain().setTextAlign("left").run();
        break;
    }
  }, [textAlign]);

  function handleLink() {
    const selectedAnchor = getSelectedAnchorElement();
    const selectedText = getSelectedEditorText(editor);
    const range = getSelectedRange();

    if (!range) return;
    if (!selectedAnchor && !selectedText) return;

    const linkURL = selectedAnchor?.getAttribute("data-id");
    const linkLabel = selectedAnchor?.getAttribute("data-label");
    const tooltipId = selectedAnchor?.getAttribute("data-tooltip-id");

    openLinkModal(
      !!selectedAnchor,
      selectedText,
      range,
      tooltipId,
      linkURL,
      linkLabel
    );
  }

  return (
    <Container
      onMouseDown={(e) => {
        setOpenDropView(false);
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div
        style={{
          width: "fit-content",
          display: "flex",
          alignItems: "center",
          gridGap: "8px",
        }}
      >
        {[
          {
            handler: handleBold,
            icon: <BoldIcon />,
            type: "bold",
            text: "Bold",
          },
          {
            handler: handleItalic,
            icon: <ItalicIcon />,
            type: "italic",
            text: "Italic",
          },
        ].map(({ handler, icon, type, text }) => (
          <EditToolbarButton
            key={type}
            onClick={() => handler(editor)}
            isActive={isElementActive(type as any, editor)}
            tooltipText={text}
            tooltipId={type}
            editor={editor}
          >
            {icon}
          </EditToolbarButton>
        ))}

        <EditToolbarButton
          onClick={() => setOpenDropView(true)}
          tooltipText="Choose text style"
          tooltipId="chooseTextStyle"
          editor={editor}
          isOpenDropView={openDropView}
        >
          <Fragment>
            <FormatTextIcon />
            {openDropView && (
              <div ref={wrapperRef}>
                <NotePopoverFormatting editor={editor} />
              </div>
            )}
          </Fragment>
        </EditToolbarButton>

        <HeadingButton editor={editor} />

        {disableTaskList && (
          <ToolbarItem
            onMouseDown={(e) => {
              if (!editor.isFocused) return;
              editor.commands.toggleTaskList();
              e.preventDefault();
              e.stopPropagation();
            }}
            data-tip
            key={"makeChecklist"}
            data-for="makeChecklist"
          >
            <IconWrapper>
              <ChecklistIcon height={45} />
            </IconWrapper>
            <ReactTooltip
              id="makeChecklist"
              place="bottom"
              effect="solid"
              backgroundColor="#333333"
              arrowColor="transparent"
              css={css`
                ${TooltipStyles} margin-left: 43px !important;
              `}
            >
              <TooltipText>Make a checklist</TooltipText>
            </ReactTooltip>
          </ToolbarItem>
        )}

        <EditToolbarButton
          editor={editor}
          tooltipId="align"
          tooltipText="Align"
          onClick={() => handleAlignTypes()}
        >
          {getAlignItem}
        </EditToolbarButton>

        <ListButton editor={editor} />

        <EditToolbarButton
          editor={editor}
          tooltipId="addLink"
          tooltipText="Add a link"
          onClick={() => handleLink()}
        >
          <LinkAttachIcon />
        </EditToolbarButton>
      </div>

      <div
        style={{ width: 1, height: 20, background: "#DDDDDD", margin: "0 8px" }}
      />

      <div
        style={{
          width: "fit-content",
          display: "flex",
          alignItems: "center",
          gridGap: "8px",
        }}
      >
        {/* TODO: This Quote button is already correct layout-wise, but it
            still need to create his functionality in tiptap
          */}
        {/* <ToolbarItem
          onClick={() => {}}
          data-tip
          key={"quote"}
          data-for="quote"
          onMouseEnter={() =>
            setToolbarOptionsHover((prevState) => ({
              ...prevState,
              isQuote: true,
            }))
          }
          onMouseLeave={() =>
            setToolbarOptionsHover((prevState) => ({
              ...prevState,
              isQuote: false,
            }))
          }
        >
           <IconWrapper isHovered={toolbarOptionsHover.isQuote}>
            <QuoteIcon />
          </IconWrapper>
          <ReactTooltip
            id="quote"
            place="bottom"
            effect="solid"
            backgroundColor="#333333"
            arrowColor="transparent"
            css={css`
              ${TooltipStyles} margin-left: 15px !important;
            `}
          >
            <TooltipText>Quote</TooltipText>
          </ReactTooltip>
        </ToolbarItem> */}

        <EditToolbarButton
          editor={editor}
          tooltipId="indent"
          tooltipText="Indent"
          onClick={() => editor.chain().setBlockquote().run()}
        >
          <IndentIcon />
        </EditToolbarButton>

        <EditToolbarButton
          editor={editor}
          tooltipId="outdent"
          tooltipText="Outdent"
          onClick={() => editor.chain().unsetBlockquote().run()}
        >
          <OutdentIcon />
        </EditToolbarButton>

        <EditToolbarButton
          editor={editor}
          tooltipId="insertHorizontalSeparator"
          tooltipText="Insert horizontal separator"
          onClick={() => editor.chain().setHorizontalRule().run()}
        >
          <HorizontalSeparatorIcon />
        </EditToolbarButton>

        {mode === "campaign" && (
          <ToolbarItem data-tip key={"mergeFilds"} data-for="mergeFilds">
            <MergeFields editor={editor} isCampaign>
              <div></div>
            </MergeFields>
          </ToolbarItem>
        )}
      </div>
    </Container>
  );
}
